import { useAppStore } from '@/store/appStore';
import { useMenuStore } from '@/store/menuStore';
import {
  Button,
  Flex,
  Icons,
  Layout,
  Menu,
  theme,
  Tooltip,
  Typography,
} from '@contiple/antd-ds';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Setting from './Setting';

const { Sider } = Layout;
const { Text } = Typography;

const SiderBar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    lnbExpanded,
    setLnbExpanded,
    firstSelectedMenu,
    setFirstSelectedMenu,
    selectedMenus,
    setSelectedMenus,
    secondMenuTitle,
  } = useAppStore();

  const { token } = theme.useToken();
  const { menuItems, menuPathMap } = useMenuStore();

  const getCurrentMenuPath = () => {
    return menuPathMap.get(location.pathname)?.menuPath || location.pathname;
  };

  const findCurrentMenu = (path: string) => {
    const currentMenu = menuPathMap.get(path);
    if (currentMenu?.parent) {
      return findCurrentMenu(currentMenu.parent);
    }
    return currentMenu || null;
  };

  // Calculate current menu and first selected menu based on current path
  const getCurrentMenuInfo = () => {
    const currentPath = getCurrentMenuPath();

    const currentMenu = findCurrentMenu(currentPath);

    const selectedMenu = currentMenu;

    const foundFirstMenu = currentMenu?.key || 'dashboard';

    return { selectedMenu, foundFirstMenu };
  };

  const handleMenuClick = (key: string) => {
    setFirstSelectedMenu(key);
    if (selectedMenus[key]?.[1]) {
      navigate(selectedMenus[key][1]?.path || '/');
    } else {
      const menuItem = menuPathMap.get(key);
      if (menuItem?.children && menuItem.children.length > 0) {
        const firstChild = menuItem.children[0];
        navigate(firstChild?.default || firstChild?.path || '/');
      } else if (menuItem?.path) {
        navigate(menuItem?.path || '/');
      }
    }
  };

  useEffect(() => {
    const { foundFirstMenu } = getCurrentMenuInfo();
    if (firstSelectedMenu !== foundFirstMenu) {
      setFirstSelectedMenu(foundFirstMenu);
    }
  }, [location.pathname, menuPathMap]);

  // Get current menu info for rendering
  const { selectedMenu: currentMenu } = getCurrentMenuInfo();

  const handleSubMenuClick = (key: string) => {
    setSelectedMenus(
      {
        [firstSelectedMenu]: { 1: menuPathMap.get(key) },
      },
      firstSelectedMenu
    );
    navigate(key);
  };

  const handleMenuBackClick = (key: string) => {
    setFirstSelectedMenu(key);
    const menuItem = menuPathMap.get(key);
    setSelectedMenus(
      {
        [key]: {},
      },
      key
    );
    if (menuItem?.children && menuItem.children.length > 0) {
      const firstChild = menuItem.children[0];
      navigate(firstChild?.default || firstChild?.path || '/');
    } else if (menuItem?.path) {
      navigate(menuItem?.path || '/');
    }
  };

  const getTitle = () => {
    return (
      secondMenuTitle[firstSelectedMenu] ||
      menuPathMap.get(firstSelectedMenu)?.name ||
      ''
    );
  };

  return (
    <>
      {/* LNB - First Level */}
      <Sider
        width={48}
        style={{
          borderRight: `1px solid ${token.custom.menu.colorSplit}`,
        }}>
        <Flex vertical gap={12} style={{ margin: '18px 8px 0' }}>
          {menuItems.map((item) => (
            <Tooltip key={item.key} title={item.name} placement="right">
              <Flex
                justify="center"
                align="center"
                style={{
                  width: '32px',
                  height: '32px',
                  padding: '6.5px 6px 5.5px',
                  cursor: 'pointer',
                  borderRadius: '8px',
                  backgroundColor:
                    firstSelectedMenu === item.key
                      ? token.custom.menu.itemSelectedBgStrong
                      : 'transparent',
                }}
                onClick={() => handleMenuClick(item.key)}>
                {item.icon}
              </Flex>
            </Tooltip>
          ))}

          <Flex
            justify="center"
            align="center"
            style={{
              position: 'absolute',
              bottom: '8px',
              left: 0,
              right: 0,
            }}>
            <Setting />
          </Flex>
        </Flex>
      </Sider>

      {/* LNB - Second Level */}
      {lnbExpanded &&
        !menuPathMap.get(getCurrentMenuPath())?.onlyShowParent &&
        currentMenu?.children && (
          <Sider
            width={200}
            theme="light"
            style={{
              borderRight: `1px solid ${token.custom.menu.colorSplit}`,
            }}>
            <Flex vertical gap={16} style={{ padding: '0 8px' }}>
              <Flex
                align="center"
                justify="space-between"
                gap={4}
                style={{
                  height: '54px',
                  padding: '18px 8px 20px 4px',
                  lineHeight: '54px',
                }}>
                {/* 뒤로 가기 버튼은 조직 관리 메뉴에서만 노출 (QA-347) */}
                {currentMenu?.path === '/organization' && (
                  <Icons.ChevronLeft
                    size={18}
                    onClick={() => handleMenuBackClick(firstSelectedMenu)}
                    style={{ cursor: 'pointer', marginBottom: 4 }}
                  />
                )}
                <Text strong ellipsis style={{ flex: 1 }}>
                  {getTitle()}
                </Text>
                <Button
                  type="text"
                  icon={<Icons.MenuFoldOutlinedIcon />}
                  onClick={() => setLnbExpanded(false)}
                />
              </Flex>
              <Menu
                mode="inline"
                selectedKeys={[getCurrentMenuPath()]}
                onClick={({ key }) => handleSubMenuClick(key)}
                style={{
                  border: 0,
                  padding: '0px',
                  margin: '0px',
                  backgroundColor: 'transparent',
                  borderRadius: 6,
                }}
                items={currentMenu?.children
                  ?.filter((child) => !child.onlyShowParent)
                  ?.map((child) => ({
                    key: child.key,
                    label: child.name,
                  }))}
              />
            </Flex>
          </Sider>
        )}
    </>
  );
};

export default SiderBar;
